<template>
  <!-- Pricing Tiers Modal -->
  <div v-if="show" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="$emit('close')">
    <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white" @click.stop>
      <div class="mt-3">
        <!-- Modal Header -->
        <div class="flex items-center justify-between pb-4 border-b border-gray-200">
          <h3 class="text-lg font-medium text-gray-900">
            {{ $t('Preisstufen') }}
          </h3>
          <button
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="mt-4">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t('Stufe') }}
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t('Mindestmenge') }}
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t('Rabatt') }}
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {{ $t('Status') }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr 
                  v-for="tier in sortedTiers" 
                  :key="tier.name"
                  :class="tier.name === currentTier?.name ? 'bg-green-50' : 'bg-white'"
                >
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <span 
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="tier.name === currentTier?.name ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                      >
                        {{ tier.name }}
                      </span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ tier.minQuantity }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span class="font-medium text-green-600">{{ tier.discount }}%</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span v-if="tier.name === currentTier?.name" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {{ $t('Aktuell') }}
                    </span>
                    <span v-else-if="currentQuantity >= tier.minQuantity" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {{ $t('Berechtigt') }}
                    </span>
                    <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {{ $t('Nicht erreicht') }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Current Status -->
          <div class="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-blue-800">
                  {{ $t('Aktueller Status') }}
                </h3>
                <div class="mt-2 text-sm text-blue-700">
                  <p>
                    {{ $t('Gesamtmenge im Warenkorb') }}: <span class="font-medium">{{ currentQuantity }}</span>
                  </p>
                  <p>
                    {{ $t('Aktuelle Preisstufe') }}: <span class="font-medium">{{ currentTier?.name }} ({{ currentTier?.discount }}% {{ $t('Rabatt') }})</span>
                  </p>
                  <p v-if="nextTier">
                    {{ $t('Nächste Preisstufe') }}: <span class="font-medium">{{ nextTier.name }}</span> 
                    ({{ $t('benötigt') }} {{ nextTier.minQuantity - currentQuantity }} {{ $t('weitere Artikel') }})
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="mt-6 flex justify-end">
          <button
            @click="$emit('close')"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sonnenglas-600 hover:bg-sonnenglas-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
          >
            {{ $t('Schließen') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface PricingTier {
  name: string
  discount: number
  minQuantity: number
  minQuantityPerProduct: number
}

interface Props {
  show: boolean
  tiers: PricingTier[]
  currentTier?: PricingTier
  currentQuantity: number
}

interface Emits {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const sortedTiers = computed(() => {
  return [...props.tiers].sort((a, b) => b.minQuantity - a.minQuantity)
})

const nextTier = computed(() => {
  if (!props.currentTier) return null
  
  const currentIndex = sortedTiers.value.findIndex(tier => tier.name === props.currentTier?.name)
  if (currentIndex > 0) {
    return sortedTiers.value[currentIndex - 1]
  }
  return null
})
</script>